/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { useAuth, useSilentLogin } from "./auth.hooks";

/**
 * Runs the session check once per page load, for every route.
 *
 * `useAuth` used to be mounted per page template, so whether a stale session was
 * ever noticed depended on which template rendered the page. On
 * epicgames-website the news index renders through `@cosmos/feed` rather than a
 * `next-templates` provider, so it never ran the check at all — and that is the
 * highest-traffic route on the site. A user whose session had gone bad stayed
 * broken there indefinitely, while the same session repaired itself the moment
 * they opened an article, which is what made this so confusing to diagnose.
 *
 * Mounting it in the layout makes the check structural instead of something each
 * new template has to remember to opt into.
 *
 * Renders nothing. Safe to mount alongside the existing per-template `useAuth`
 * calls: they share one SWR key, so the request is deduped rather than repeated.
 * Must sit inside `AccountProvider`, because the refresh it triggers writes
 * through that context.
 *
 * There is deliberately no `disable` prop. A site that does not use Epic
 * accounts should simply not mount this. The prop existed for one round and
 * every call site fed it `componentContext.enableAuth`, which is a `"use
 * client"` module read from an async Server Component — that throws at render.
 * Mount it or do not; there is nothing for a root layout to branch on.
 *
 * It also follows the server's cross-domain silent sign-in offer (`useSilentLogin`), so despite
 * rendering nothing it can navigate the tab — once, through `/auth/login?prompt=none`, only when
 * the user is signed in to Epic elsewhere and has nothing unsaved on the page. Mounting it therefore
 * opts the whole site into that, regardless of any page's `enableAuth`; only set
 * `layout.accountAuthSync` on sites that use Epic accounts.
 */
export const AccountAuthSync = (): null => {
	useSilentLogin(useAuth());
	return null;
};
